// this is stored off so we not need to check each time
import { Guid } from "guid-typescript";
import { dereferenceSync } from "dereference-json-schema";
import { DataTypes } from "./Data/DataTypes";
import { IDataDefinition } from "./Data/Definitions/IDataDefinition";
import { UndefinedDataDefinition } from "./Data/Definitions/UndefinedDataDefinition";
import { FileDataDefinition } from "./Data/Definitions/FileDataDefinition";
import { ObjectDataDefinition } from "./Data/Definitions/ObjectDataDefinition";
import { DateDataDefinition } from "./Data/Definitions/DateDataDefinition";
import { NumberDataDefinition } from "./Data/Definitions/NumberDataDefinition";
import { StringDataDefinition } from "./Data/Definitions/StringDataDefinition";
import { BooleanDataDefinition } from "./Data/Definitions/BooleanDataDefinition";
import { DataDefinition } from "./Data/DataDefinition";
import { AddFieldToDataStructure } from "./Data/ExecutionData/AddFieldToDataStructure";
import { DeleteFieldToDataStructure } from "./Data/ExecutionData/DeleteFieldToDataStructure";
import { EditFieldToDataStructure } from "./Data/ExecutionData/EditFieldToDataStructure";
import { DataStructureDifferences } from "./Data/ExecutionData/DataStructureDifferences";
import {
    ArrayAddDataMemberAccessor,
    ArrayAddFirstDataMemberAccessor,
    ArrayFirstDataMemberAccessor,
    ArrayItemDataMemberAccessor,
    ArrayLastDataMemberAccessor,
    IDataMemberAccessor,
    PropertyDataMemberAccessor,
} from "./DataMemberAccessor";
import { ValidationIssue } from "./IDesignValidationProvider";
import { FileData } from "./FileData";
import TurndownService from "turndown";
import { XMLBuilder, XMLParser } from "fast-xml-parser";
import type { AppGlueAny } from "./AppGlueAny";

export enum DataCompareValueType {
    NOCHANGE = "no change",
    ADDED = "added",
    DELETED = "deleted",
    CHANGED = "changed",
    CHILD_DIFFERENCE = "child differences",
}

export class DataCompareValues {
    parent: DataCompareValues | undefined;
    children: DataCompareValues[] = [];

    changeType: DataCompareValueType = DataCompareValueType.NOCHANGE;
    origValue?: AppGlueAny;
    currentValue?: object;
}

export class DataUtilities {
    static getDataDefinitionFromData(data: object): IDataDefinition | undefined {
        return DataDefinition.createFieldFromData(data);
    }

    static getDataDefinitionListFromData(data: object[]): IDataDefinition[] {
        let output: IDataDefinition[] = [];

        for (let d of data) {
            let dd = this.getDataDefinitionFromData(d);
            if (dd) output.push(dd);
        }

        return output;
    }

    static getDataDefinitionDataFromDefinitionList(data: IDataDefinition[]): object[] {
        let output: object[] = [];

        for (let d of data) {
            let dd = d.getStorageData();
            if (dd) output.push(dd);
        }

        return output;
    }

    static compare(v1: object, v2: object): boolean {
        // we are cloning to get rid of stuff we do not want to compare
        // we can not use this function as it's for node (backend).
        // return util.isDeepStrictEqual(this.clone(v1), this.clone(v2));

        // it's not returning true in following case
        // const a = {a: 1, b: 2};
        // const b = {a: 1, b: 2};
        // Object.is(a, b) => false
        // Object.is(a, a) => true
        // return Object.is(this.clone(v1), this.clone(v2));
        v1 = this.clone(v1);
        v2 = this.clone(v2);

        const keys1 = Object.keys(v1);
        const keys2 = Object.keys(v2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            const val1 = Reflect.get(v1, key);
            const val2 = Reflect.get(v2, key);
            const areObjects = val1 != null && typeof val1 === "object" && val2 != null && typeof val2 === "object";

            if (areObjects) {
                if (!this.compare(val1, val2)) return false;
            }

            if (!areObjects && val1 !== val2) {
                return false;
            }
        }
        return true;
    }

    static clone(value: object): object {
        let output = {};

        for (let i in value) {
            // these are special properties we do not want in output
            if (!i.startsWith("_____")) Reflect.set(output, i, Reflect.get(value, i));
        }

        return output;
    }

    static addStorageData(forClass: object, data: object | undefined, ...fields: string[]): object {
        if (!data) data = {};
        for (let f of fields) {
            Reflect.set(data, f, Reflect.get(forClass, f));
        }
        return data;
    }

    static setStorageData(forClass: object, data: object, ...fields: string[]) {
        if (!data) return;
        for (let f of fields) {
            Reflect.set(forClass, f, Reflect.get(data, f));
        }
    }

    static spreadData(value: object, dataToSpread: object, exclude?: string[]): void {
        for (let i in dataToSpread) {
            if (exclude && exclude.length !== 0) {
                if (exclude.indexOf(i) !== -1) continue;
            }

            Reflect.set(value, i, Reflect.get(dataToSpread, i));
        }
    }

    static spreadDeepData(value: object, dataToSpread: object, exclude?: string[]): void {
        for (let i in dataToSpread) {
            if (exclude && exclude.length !== 0) {
                if (exclude.indexOf(i) !== -1) continue;
            }

            let newValue = Reflect.get(dataToSpread, i);
            if (typeof newValue === "object") {
                let copiedValue = Array.isArray(newValue) ? [] : {};
                this.spreadDeepData(copiedValue, newValue);
                Reflect.set(value, i, copiedValue);
            } else {
                Reflect.set(value, i, newValue);
            }
        }
    }

    static spreadDataWithoutFunction(value: object, dataToSpread: object, exclude?: string[]): void {
        for (let i in dataToSpread) {
            if (exclude && exclude.length !== 0) {
                if (exclude.indexOf(i) !== -1) continue;
            }

            let val = Reflect.get(dataToSpread, i);
            if (val instanceof Function) continue;

            Reflect.set(value, i, val);
        }
    }

    static generateUniqueId(): string {
        return Guid.create().toString();
    }

    static isValidDate(possibleDate: AppGlueAny): boolean {
        // todo: might need to do more here
        return possibleDate instanceof Date;
    }

    static isValidFile(possibleFile: object): boolean {
        return this.compareObjectStructure(possibleFile, new FileData("test.txt", new ArrayBuffer(0)));
    }

    static getElapsedTime(startTime: Date, endTime: Date): string {
        let diff = endTime.getTime() - startTime.getTime();

        let msc = diff % 1000;

        let ss = Math.floor(diff / 1000);

        if (ss === 0) {
            return `${msc} ms`;
        }

        let mm = Math.floor(ss / 60);

        if (mm === 0) {
            return `${ss} second${ss > 1 ? "s" : ""}, ${msc} ms`;
        }

        ss = ss % 60;

        let hh = Math.floor(mm / 60);

        if (hh === 0) {
            return `${mm} minute${mm > 1 ? "s" : ""}, ${ss} second${ss > 1 ? "s" : ""}`;
        }

        mm = mm % 60;

        return `${`${hh}`.padStart(2, "0")}:${`${mm}`.padStart(2, "0")}:${`${ss}`.padStart(2, "0")}`;
    }

    // ensure object has all the values and functions that matchesObject has
    static compareObjectStructure(object: object, matchesObject: object): boolean {
        const keys1 = Object.keys(object);
        const keys2 = Object.keys(matchesObject);

        for (let key of keys2) {
            if (keys1.indexOf(key) === -1) {
                return false;
            }
        }

        return true;
    }

    static isAssignable(val: AppGlueAny, toVal: AppGlueAny): boolean {
        // we cannot make judgement
        if (val === null || val === undefined || toVal === null || toVal === undefined) return true;

        // array types do not match
        if (Array.isArray(val) !== Array.isArray(toVal)) return false;

        // types do not match
        if (typeof val != typeof toVal) return false;

        // if object, do structure compare
        if (typeof val === "object") return true;

        return true;
    }

    static isDateString(checkVal: string): boolean {
        const dateFormat = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;

        return dateFormat.test(checkVal);
    }

    static getDateFromString(s: string): Date {
        return new Date(Date.parse(s));
    }

    // todo: add cache?
    static splitPath(path: string): IDataMemberAccessor[] {
        let dataMembers: IDataMemberAccessor[] = [];

        let pathPieces = path.split(".");

        for (let p of pathPieces) {
            if (p.indexOf("[") !== -1) {
                // remove ]
                p = p.replace("]", "");
                // split again
                let subPieces = p.split("[");

                if (subPieces[subPieces.length - 1] === "") subPieces.splice(subPieces.length - 1, 1);

                if (subPieces.length === 1) {
                    // this is case where [] means we are goign to return all the array pieces
                    dataMembers.push(new PropertyDataMemberAccessor(subPieces[0]));
                } else {
                    // first piece = property
                    dataMembers.push(new PropertyDataMemberAccessor(subPieces[0], true));

                    // second piece = index or first or last
                    if (subPieces[1] === "add") {
                        dataMembers.push(new ArrayAddDataMemberAccessor());
                    } else if (subPieces[1] === "addfirst") {
                        dataMembers.push(new ArrayAddFirstDataMemberAccessor());
                    } else if (subPieces[1] === "first") {
                        dataMembers.push(new ArrayFirstDataMemberAccessor());
                    } else if (subPieces[1] === "last") {
                        dataMembers.push(new ArrayLastDataMemberAccessor());
                    } else if (subPieces[1] === "all") {
                        // do nothing
                    } else {
                        dataMembers.push(new ArrayItemDataMemberAccessor(+subPieces[1]));
                    }

                    // todo:
                    // delete
                    // multiple arroy
                    // [add][0]
                }
            } else {
                dataMembers.push(new PropertyDataMemberAccessor(p));
            }
        }

        return dataMembers;
    }

    // rules
    // - path is separated by .
    // - [] returns all items of array
    // - . on array without specifying that we want all items '[]' returns the value of the property
    // - . after [] returns new array with the items of that particular property
    // - [first] and [last] return the first and last item
    // eslint-disable-next-line
    static get(from: object, path: string, defaultvalue?: AppGlueAny): AppGlueAny {
        let pieces = this.splitPath(path);

        let v = from;

        for (let p of pieces) {
            v = p.get(v);

            if (v === undefined || v === null) return undefined;
        }

        return v;
    }

    static getString(from: object, path: string, defaultValue?: string): string | undefined {
        let val = this.get(from, path, defaultValue);

        if (typeof val === "object") {
            return JSON.stringify(val);
        } else if (val !== undefined) {
            return val + "";
        }
        return undefined;
    }

    static set(
        from: object,
        path: string,
        value: AppGlueAny,
        throwExceptionIfTargetIsNull: boolean = true,
        notCheckType: boolean = false,
        correctValues: boolean = false,
    ): void {
        let pieces = this.splitPath(path);

        let v: object | undefined = from;
        let lastPiece = pieces[pieces.length - 1];
        // removes last item from array
        pieces = pieces.slice(0, -1);

        for (let p of pieces) {
            let parentV = v;

            v = p.get(v);

            if (v === undefined || v === null) {
                if (correctValues) {
                    if (p.list) {
                        v = [];
                    } else {
                        v = {};
                    }
                    p.set(parentV, v);
                    continue;
                }

                if (throwExceptionIfTargetIsNull)
                    throw new Error(`an parent item to item being set is null or undefined ${path}`);
                return;
            }
        }

        if (!notCheckType) {
            let currentVal = lastPiece.get(v);
            if (currentVal && typeof currentVal === "string" && typeof value !== "string" && value) value = value + "";

            if (!this.isAssignable(value, currentVal))
                throw new Error(
                    `unassignable: cannot replace ${typeof currentVal} ${currentVal} with ${typeof value} ${value}`,
                );
        }

        lastPiece.set(v, value);
    }

    static getFieldFromDataDefinitionByName(fields: IDataDefinition[], path: string): IDataDefinition | null {
        let fls = fields;
        // todo: throw exception
        if (fls.length === 0) return null;

        let field = fls?.find((f) => f.name === path);
        if (field) return field;

        if (path.includes(".")) {
            let pieces = path.split(".");
            let [parent] = pieces.splice(0, 1);
            let parentField = fls.find((f) => f.name === parent);

            if (parentField) {
                if (pieces.length === 0) {
                    return parentField;
                }
                const subFields = Reflect.get(parentField, "fields");

                if (subFields && subFields.length) {
                    return this.getFieldFromDataDefinitionByName(subFields, pieces.join("."));
                }
            }
        }
        throw new Error(`Can not find the field by path: ${path}`);
    }

    static filterFieldsByName(fields: IDataDefinition[], searchStr: string): IDataDefinition[] {
        let result: IDataDefinition[] = [];
        fields.forEach((field: IDataDefinition) => {
            const subfields = Reflect.get(field, "fields");
            let isAdded = false;
            if (subfields && subfields.length > 0) {
                const searchedSubFields = this.filterFieldsByName(subfields, searchStr);
                if (searchedSubFields.length > 0) {
                    isAdded = true;
                    result.push({
                        ...field,
                        fields: searchedSubFields,
                    });
                }
            }
            if (!isAdded)
                if (field.name.includes(searchStr)) {
                    result.push(field);
                }
        });

        return result;
    }

    static createDataByType(type: DataTypes, name?: string): IDataDefinition {
        let def: IDataDefinition | null = null;
        switch (type) {
            case DataTypes.BOOLEAN:
                def = new BooleanDataDefinition(name);
                break;
            case DataTypes.STRING:
                def = new StringDataDefinition(name);
                break;
            case DataTypes.NUMBER:
                def = new NumberDataDefinition(name);
                break;
            case DataTypes.DATE:
                def = new DateDataDefinition(name);
                break;
            case DataTypes.OBJECT:
                def = new ObjectDataDefinition(name);
                break;
            case DataTypes.FILE:
                def = new FileDataDefinition(name);
                break;
        }

        if (!def) {
            def = new UndefinedDataDefinition(name);
        }

        return def;
    }

    static getAllSubFieldsByType(data: { fields: IDataDefinition[] }, type: DataTypes): IDataDefinition[] {
        const fields: IDataDefinition[] = data.fields.filter((f) => f.type === type);
        return fields;
    }

    static getAllDeepSubFieldsByType(data: { fields: IDataDefinition[] }, type: DataTypes, basepath?: string) {
        let result: { path: string | undefined; def: IDataDefinition }[] = [];

        let name = Reflect.get(data, "name") as string;
        let currentpath: string | undefined;

        if (name && basepath) {
            currentpath = `${basepath}.${name}`;
        } else if (name) {
            currentpath = name;
        }

        let subfields = this.getAllSubFieldsByType(data, type);
        subfields.forEach((s) => {
            result.push({
                path: currentpath,
                def: s,
            });
        });

        let childrenObjFs = this.getAllSubFieldsByType(data, DataTypes.OBJECT) as ObjectDataDefinition[];
        childrenObjFs.forEach((c) => {
            result = [...result, ...this.getAllDeepSubFieldsByType(c, type, currentpath)];
        });

        return result;
    }

    static isIDataDefinition(def: IDataDefinition | DataDefinition) {
        return Object.keys(def).indexOf("type") !== -1;
    }

    // specific property values should be unique. If other case, it's overwrited by later elem.
    static convertArrayObjectToObjectByProperty(arr: Array<object>, property: string) {
        let result: { [key: string]: object } = arr.reduce((r: { [key: string]: object }, cur: object) => {
            r[Reflect.get(cur, property)] = cur;
            return r;
        }, {});
        return result;
    }

    static getTypesMatch(
        data: DataDefinition,
        path: string,
        requiredType: DataTypes | ObjectDataDefinition | undefined,
        isList?: boolean,
    ): boolean {
        if (!requiredType && !isList) return true;

        let selectedField = DataUtilities.getFieldFromDataDefinitionByName(data.fields, path) as IDataDefinition;

        if (!selectedField) {
            return false;
        }

        if (isList !== undefined) {
            if (isList !== selectedField.list) return false;
        }

        if (!requiredType) return true;

        if (requiredType instanceof ObjectDataDefinition) {
            if (selectedField.type !== DataTypes.OBJECT) return false;
            // compare fields

            return this.compareStructureToStructure(requiredType, selectedField as ObjectDataDefinition);
        } else if (requiredType !== selectedField.type) {
            if (requiredType === DataTypes.STRING && selectedField.type !== DataTypes.OBJECT) {
                // anything can be converted to a string
                return true;
            } else {
                return false;
            }
        }

        return true;
    }

    static compareStructureToStructure(required: ObjectDataDefinition, dataToCompare: ObjectDataDefinition): boolean {
        if ((required.list || dataToCompare.list) && required.list !== dataToCompare.list) {
            return false;
        }
        let dataToCompareFieldsObj = this.convertArrayObjectToObjectByProperty(dataToCompare.fields, "name");

        return !required.fields.find((f1: IDataDefinition) => {
            if (!f1.name) return false;
            const f2 = Reflect.get(dataToCompareFieldsObj, f1.name) as IDataDefinition;
            if (!f2) return true;

            if (f1.type !== f2.type) return true;

            if (f1.type === DataTypes.OBJECT) {
                return !this.compareStructureToStructure(f1 as ObjectDataDefinition, f2 as ObjectDataDefinition);
            }
            return false;
        });
    }

    static compareStructureToPath(
        required: ObjectDataDefinition,
        dataToCompare: DataDefinition,
        path: string,
    ): boolean {
        if (path === "") return false;

        let field = this.getFieldFromDataDefinitionByName(dataToCompare.fields, path) as ObjectDataDefinition;

        let subfields = (Reflect.get(field, "fields") || []) as IDataDefinition[];
        if (subfields.length < required.fields.length) return false;
        return this.compareStructureToStructure(required, field);
    }

    static compareValueToDataDefinition(
        value: AppGlueAny,
        dataDefinition: IDataDefinition,
    ): ValidationIssue | undefined {
        if (value === undefined) return new ValidationIssue("Undefined Value");

        return dataDefinition.verifyValueType(value);
    }

    // todo
    // 1. might need the confirmation flag for data. ie: reset, remain.
    // 2. reset for list field
    static changeDataDefinitionTypeByPath(data: DataDefinition, totype: DataTypes, path: string) {
        let names = path === "" ? [] : path.split(".");

        let olddef = this.getFieldFromDataDefinitionByName(data.fields, path) as IDataDefinition;
        if (olddef.type === totype) return;

        let newdef = this.createDataByType(totype, olddef.name);

        if (names.length > 1) {
            names = names.slice(0, -1);
            let parentdef = this.getFieldFromDataDefinitionByName(data.fields, names.join(".")) as ObjectDataDefinition;
            if (parentdef) {
                parentdef.fields = parentdef.fields.map((f) => {
                    if (f.name === olddef.name) return newdef;
                    return f;
                });
            }
        } else if (names.length > 0) {
            data.fields = data.fields.map((f) => {
                if (f.name === olddef.name) return newdef;
                return f;
            });
        }

        // reset the original value
        try {
            this.set(data.getValueObject(), path, undefined);
        } catch { }
    }

    static addDataDefinitionByPath(data: DataDefinition, totype: DataTypes, path: string, name?: string) {
        if (name && this.validateDataDefinitionNameByPath(data, `${path === "" ? "" : `${path}.`}${name}`, name)) {
            throw new Error(`Can not create a field with name: ${name}`);
        }

        let newdef = this.createDataByType(totype, name);

        let names = path === "" ? [] : path.split(".");

        let parentdef: DataDefinition | ObjectDataDefinition | null = null;

        if (names.length > 0) {
            parentdef = this.getFieldFromDataDefinitionByName(data.fields, path) as ObjectDataDefinition;
        } else {
            parentdef = data;
        }

        parentdef.fields.push(newdef);
    }

    static validateDataDefinitionNameByPath(
        data: DataDefinition,
        path: string,
        name: string,
        availableCount = 0,
    ): string | null {
        if (name === "") {
            return "field name can not be empty.";
        }

        if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
            return "field name is not valid.";
        }

        let parentdef: DataDefinition | ObjectDataDefinition | null = null;
        if (path.includes(".")) {
            let names = path.split(".");
            names.splice(-1, 1);
            parentdef = this.getFieldFromDataDefinitionByName(data.fields, names.join(".")) as ObjectDataDefinition;
        } else {
            parentdef = data;
        }
        if ((parentdef.fields || []).filter((f: IDataDefinition) => f.name === name).length > availableCount) {
            return "field name should be unique at same level.";
        }
        return null;
    }

    static changeDataDefinitionNameByPath(data: DataDefinition, newname: string, path: string) {
        if (this.validateDataDefinitionNameByPath(data, path, newname)) {
            throw new Error(`Can not change the field with name: ${newname}`);
        }

        let field = this.getFieldFromDataDefinitionByName(data.fields, path) as IDataDefinition;
        if (!field) throw new Error(`Can not find the field with path: ${path}`);
        if (field.name === newname) {
            return;
        }
        let oldname = field.name;
        field.name = newname;
        let val = this.get(data.getValueObject(), path);

        let idx = path.lastIndexOf(oldname);
        let newpath = path.slice(0, idx) + path.slice(idx).replace(oldname, newname);

        this.set(data.getValueObject(), newpath, val, false);
        this.set(data.getValueObject(), path, undefined, false);
    }

    static changeFieldPositionByPath(data: DataDefinition, newindex: number, path: string) {
        let names = path === "" ? [] : path.split(".");
        let def = this.getFieldFromDataDefinitionByName(data.fields, path) as IDataDefinition;
        let parentdef: {
            fields: IDataDefinition[];
        } | null = null;

        if (names.length > 1) {
            names = names.slice(0, -1);
            parentdef = this.getFieldFromDataDefinitionByName(data.fields, names.join(".")) as ObjectDataDefinition;
        } else {
            parentdef = data;
        }
        if (parentdef) {
            let oldindex = parentdef.fields.map((f: IDataDefinition) => f.name).indexOf(def.name);

            if (oldindex !== newindex) {
                parentdef.fields.splice(oldindex, 1);
                parentdef.fields.splice(newindex, 0, def);
            }
        }
    }

    static createDataDefinitionFromDataType(dataType: DataTypes) {
        let dd: IDataDefinition;
        switch (dataType) {
            case DataTypes.BOOLEAN:
                dd = new BooleanDataDefinition();
                break;
            case DataTypes.DATE:
                dd = new DateDataDefinition();
                break;
            case DataTypes.FILE:
                dd = new FileDataDefinition();
                break;
            case DataTypes.NUMBER:
                dd = new NumberDataDefinition();
                break;
            case DataTypes.OBJECT:
                dd = new ObjectDataDefinition();
                break;
            case DataTypes.STRING:
                dd = new StringDataDefinition();
                break;
            case DataTypes.UNDEFINED:
                dd = new UndefinedDataDefinition();
                break;
        }
        return dd;
    }

    static removeFieldByPath(data: DataDefinition, path: string) {
        let names = path === "" ? [] : path.split(".");
        let def = this.getFieldFromDataDefinitionByName(data.fields, path) as IDataDefinition;
        let parentdef: {
            fields: IDataDefinition[];
        } | null = null;

        if (names.length > 1) {
            names = names.slice(0, -1);
            parentdef = this.getFieldFromDataDefinitionByName(data.fields, names.join(".")) as ObjectDataDefinition;
        } else {
            parentdef = data;
        }
        if (parentdef) {
            let oldindex = parentdef.fields.map((f: IDataDefinition) => f.name).indexOf(def.name);
            parentdef.fields.splice(oldindex, 1);
        }
    }

    static checkType(value: AppGlueAny, dataType: IDataDefinition): boolean {
        if (dataType.list && !Array.isArray(value)) {
            return false;
        }

        switch (dataType.type) {
            case DataTypes.NUMBER:
            case DataTypes.STRING:
            case DataTypes.OBJECT:
            case DataTypes.BOOLEAN:
                if (dataType.list && Array.isArray(value)) {
                    return !value.find((v) => typeof v !== dataType.type);
                }
                return typeof value === dataType.type;
            case DataTypes.DATE:
                const isDate = (v: AppGlueAny) => {
                    if (typeof v === "string") {
                        let d = new Date(v);
                        return !isNaN(d.getTime());
                    } else if (v instanceof Date) {
                        return !isNaN(v.getTime());
                    }
                    return false;
                };

                if (dataType.list && Array.isArray(value)) {
                    return !value.find((v) => !isDate(v));
                }

                return isDate(value);

            case DataTypes.FILE:
                const isFile = (v: AppGlueAny) => {
                    if (typeof v === "object") {
                        return (
                            v.file &&
                            v.file instanceof File &&
                            (typeof v.data === "string" || v.data instanceof ArrayBuffer || v.data === null)
                        );
                    }
                    return false;
                };

                if (dataType.list && Array.isArray(value)) {
                    if (dataType.list && Array.isArray(value)) {
                        return !value.find((v) => !isFile(v));
                    }
                }
                return isFile(value);
        }

        return true;
    }

    static defRefSchema(schema: object): string {
        return JSON.stringify(dereferenceSync(schema));
    }

    static getObjectDisplayText(o: object): string {
        return JSON.stringify(o);
    }

    static summerizeText(text: string, maxLen: number): string {
        if (text.length < maxLen) return text;
        else {
            return `${text.slice(0, 6)}...${text.slice(-3)}`;
        }
    }

    static containsObjectListField(data: DataDefinition | ObjectDataDefinition): boolean {
        for (let field of data.fields) {
            if (field instanceof ObjectDataDefinition) {
                if (field.list) return true;
                if (this.containsObjectListField(field)) return true;
            }
        }

        return false;
    }

    static containsObjectField(data: DataDefinition | ObjectDataDefinition): boolean {
        for (let field of data.fields) {
            if (field instanceof ObjectDataDefinition) {
                return true;
            }
        }

        return false;
    }

    static getAllSubFields(data: DataDefinition | ObjectDataDefinition): IDataDefinition[] {
        let fields: IDataDefinition[] = [];

        for (let field of data.fields) {
            fields.push(field);

            if (field instanceof ObjectDataDefinition) {
                fields.push(...this.getAllSubFields(field));
            }
        }

        return fields;
    }

    // todo: notCheckInDeep should be removed
    // it's only for version 1.
    static compareDataDefinitions(
        fields1: IDataDefinition[],
        fields2: IDataDefinition[],
        notCheckInDeep: boolean = false,
    ): DataStructureDifferences[] {
        let changes: DataStructureDifferences[] = [];
        let newFieldNames: string[] = fields2.map((nf) => nf.name);
        let currentFieldNames: string[] = fields1.map((nf) => nf.name);

        for (let idx in fields2) {
            let f = fields2[idx];

            if (!currentFieldNames.includes(f.name)) {
                changes.push(new AddFieldToDataStructure(f, f.name, parseInt(idx, 10)));
            }
        }

        fields1
            .filter((f) => !newFieldNames.includes(f.name))
            .forEach((f) => {
                changes.push(new DeleteFieldToDataStructure(f, f.name));
            });

        for (let newF of fields2) {
            if (currentFieldNames.includes(newF.name)) {
                let field = fields1.find((f) => f.name === newF.name)!;

                if (field.type !== newF.type || field.list !== newF.list) {
                    changes.push(new EditFieldToDataStructure(newF, field.name));
                } else if (field instanceof ObjectDataDefinition && newF instanceof ObjectDataDefinition) {
                    changes.push(
                        ...this.compareDataDefinitions(field.fields, newF.fields, notCheckInDeep).map((c) => {
                            c.path = `${field.name}.${c.path}`;
                            return c;
                        }),
                    );
                } else if (!notCheckInDeep) {
                    let v1 = field.getStorageData();
                    Reflect.set(v1, "owner", null);
                    let v2 = newF.getStorageData();
                    Reflect.set(v2, "owner", null);

                    if (!DataUtilities.compare(v1, v2)) {
                        changes.push(new EditFieldToDataStructure(newF, field.name));
                    }
                }
            }
        }

        return changes;
    }

    static getAllSubFieldsWithPath(
        data: DataDefinition | ObjectDataDefinition,
    ): { path: string; field: IDataDefinition }[] {
        let result: { path: string; field: IDataDefinition }[] = [];

        for (let field of data.fields) {
            result.push({
                path: field.name,
                field,
            });
            if (field instanceof ObjectDataDefinition) {
                result.push(
                    ...this.getAllSubFieldsWithPath(field).map((f) => {
                        return {
                            path: `${field.name}.${f.path}`,
                            field: f.field,
                        };
                    }),
                );
            }
        }

        return result;
    }

    static setCookieValue(key: string, value: string, untilSeconds: number = 3600) {
        const d = new Date();
        d.setTime(d.getTime() + untilSeconds * 1000);

        document.cookie = `${key}=${value};expires=${d.toUTCString()};path=/`;
    }

    static getCookie(cname: string): string {
        let name = cname + "=";
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == " ") {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    }

    static compareObjects(_orig: object, _current: object): DataCompareValues {
        // compute a => b
        // compute b => a
        // merge
        // purge

        return new DataCompareValues();
    }

    static convertHTMLToString(html: string): string {
        const turndownService = new TurndownService();
        return turndownService.turndown(html);
    }

    static getIndent(howDeep: number, spacing: string = "  "): string {
        let s = "";

        for (let i = 0; i < howDeep; i++) {
            s = s + spacing;
        }

        return s;
    }

    static jsonToObject(json: string): object {
        return JSON.parse(json);
    }

    static objectToJSON(o: object): string {
        return JSON.stringify(o);
    }

    static xmlToObject(xml: string): object {
        const parser = new XMLParser({
            ignoreAttributes: false,
            attributeNamePrefix: "@_",
        });
        return parser.parse(xml);
    }

    static objectToXML(o: object): string {
        const builder = new XMLBuilder({
            ignoreAttributes: false,
            attributeNamePrefix: "@_",
            format: true,
        });
        return builder.build(o);
    }

    static parseJSON(a: string) {
        let reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
        let rawDate = /^\d{4}-\d{2}-\d{2}$/;
        let reMsAjax = /^\/Date\((d|-|.*)\)[/|\\]$/;
        let dateParser = function (key: AppGlueAny, value: AppGlueAny) {
            if (typeof value === "string") {
                let a = reISO.exec(value);
                if (a) {
                    let d = new Date(value);
                    return DataUtilities.getDateString(d, false);
                }
                a = rawDate.exec(value);
                if (a) {
                    let b = value.split("-");
                    let d = new Date(Number.parseInt(b[0]), Number.parseInt(b[1]), Number.parseInt(b[2]));
                    return DataUtilities.getDateString(d, true);
                }
                a = reMsAjax.exec(value);
                if (a) {
                    let b = a[1].split(/[-+,.]/);
                    let d = new Date(b[0] ? +b[0] : 0 - +b[1]);
                    return DataUtilities.getDateString(d, false);
                }
            }
            return value;
        };
        let obj = JSON.parse(a, dateParser);
        return obj;
    }

    static getDateString(date: Date, ignoreHours?: boolean) {
        var year = date.getFullYear();
        var month = ("0" + (date.getMonth() + 1)).slice(-2);
        var day = ("0" + date.getDate()).slice(-2);
        var hour = ignoreHours ? "00" : ("0" + date.getHours()).slice(-2);
        var minutes = ignoreHours ? "00" : ("0" + date.getMinutes()).slice(-2);

        return year + "-" + month + "-" + day + "T" + hour + ":" + minutes;
    }

    static moveUpInArray<T>(array: T[], index: number): T[] {
        if (index === 0) {
            return array; // cannot move up first item
        }
        const newArray = [...array]; // create a copy of the original array
        // swap items
        [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
        return newArray;
    }

    static moveDownInArray<T>(array: T[], index: number): T[] {
        if (index === array.length - 1) {
            return array; // cannot move down last item
        }
        const newArray = [...array]; // create a copy of the original array
        // swap items
        [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
        return newArray;
    }

    static stringToArrayBuffer(s: string): ArrayBuffer {
        // convert string to array buffer
        let b = new Uint8Array(s.length);

        for (var i = 0; i < s.length; i++) {
            b[i] = s.codePointAt(i)!;
        }

        return b.buffer;
    }
}
